import { Reducer } from 'redux';
import { getNow } from 'src/utils/time';
import { EvidenceType } from '../../api';
import {
  OpenReportAction,
  PublicReport as OpenReportState,
  OpenReportTypes,
  PublicReport,
} from '../../types';
import { getRandomId } from '../../utils/commonFunctions';

export const openReportState = () => {
  const publicReportId = getRandomId();
  const eventId = getRandomId();

  const evidenceEvents: OpenReportState['evidence']['events'] = [
    {
      created: getNow({ returnAsDate: true }),
      updated: getNow({ returnAsDate: true }),
      id: eventId,
      report: publicReportId,
    },
  ];
  return {
    name: undefined,
    email: undefined,
    relationshipToCompany: null,
    password: '',
    confirmation: '',
    organisation: '',
    privacyStatementUrl: null,
    logo: '/assets/vault-logo.png',
    perpetrators: [],
    evidence: {
      events: evidenceEvents,
      evidence: [],
      report: {
        category: '',
        created: getNow({ returnAsDate: true }),
        id: publicReportId,
        status: 'NEW',
        updated: getNow({ returnAsDate: true }),
        relationshipToCustomer: '',
      },
    },
    category: '',
    created: getNow({ returnAsDate: true }),
    id: publicReportId,
    source: 'open-reporting',
    status: 'NEW',
    updated: getNow({ returnAsDate: true }),
  };
};

const openReportReducer: Reducer<OpenReportState, OpenReportAction> = (
  state: OpenReportState = openReportState(),
  action: OpenReportAction,
): OpenReportState => {
  const createEvidence = (
    type: EvidenceType,
    value: PublicReport['evidence']['evidence'][0]['value'],
  ) => ({
    id: getRandomId(),
    event: state.evidence.events[0].id,
    report: state.evidence.events[0].report,
    type,
    created: getNow({ returnAsDate: true }),
    updated: getNow({ returnAsDate: true }),
    value,
  });

  switch (action.type) {
    case OpenReportTypes.RESET:
    case OpenReportTypes.CLEAR:
      return {
        ...openReportState(),
        organisation: state.organisation,
        logo: state.logo,
      };
    case OpenReportTypes.SELECT_SUBMISSION_TYPE: {
      const selectedCategory = action.payload;
      return {
        ...state,
        category: selectedCategory.name,
        reportedCategory: selectedCategory.id,
        evidence: {
          ...state.evidence,
          report: {
            ...state.evidence.report,
            reportedCategory: selectedCategory.id,
            category: selectedCategory.name,
          },
        },
      };
    }
    case OpenReportTypes.SELECT_RELATIONSHIP_TYPE: {
      return {
        ...state,
        relationshipToCompany: action.payload.id,
      };
    }
    case OpenReportTypes.SET_EVENT_DATE: {
      const dateIndex = state.evidence.evidence.findIndex(
        (e) => e.type === EvidenceType.DATE,
      );
      return {
        ...state,
        evidence: {
          ...state.evidence,
          evidence:
            dateIndex > -1
              ? state.evidence.evidence.map((e, i) =>
                  i === dateIndex ? { ...e, value: action.payload } : e,
                )
              : [
                  ...state.evidence.evidence,
                  createEvidence(EvidenceType.DATE, action.payload),
                ],
        },
      };
    }
    case OpenReportTypes.SET_EVENT_TIME: {
      const timeIndex = state.evidence.evidence.findIndex(
        (e) => e.type === EvidenceType.TIME,
      );
      return {
        ...state,
        evidence: {
          ...state.evidence,
          evidence:
            timeIndex > -1
              ? state.evidence.evidence.map((e, i) =>
                  i === timeIndex ? { ...e, value: action.payload } : e,
                )
              : [
                  ...state.evidence.evidence,
                  createEvidence(EvidenceType.TIME, action.payload),
                ],
        },
      };
    }
    case OpenReportTypes.SET_EVENT_DESCRIPTION: {
      const descriptionIndex = state.evidence.evidence.findIndex(
        (e) => e.type === EvidenceType.DESCRIPTION,
      );
      return {
        ...state,
        evidence: {
          ...state.evidence,
          evidence:
            descriptionIndex > -1
              ? state.evidence.evidence.map((e, i) =>
                  i === descriptionIndex ? { ...e, value: action.payload } : e,
                )
              : [
                  ...state.evidence.evidence,
                  createEvidence(EvidenceType.DESCRIPTION, action.payload),
                ],
        },
      };
    }
    case OpenReportTypes.SET_EMAIL:
      return { ...state, email: action.payload.currentTarget.value };
    case OpenReportTypes.SET_NAME:
      return { ...state, name: action.payload.currentTarget.value };
    case OpenReportTypes.SET_PASSWORD:
      return {
        ...state,
        password: action.payload.currentTarget.value,
      };
    case OpenReportTypes.SET_CONFIRMATION:
      return {
        ...state,
        confirmation: action.payload.currentTarget.value,
      };

    case OpenReportTypes.SET_EVENT_PERPETRATORS: {
      const perpArray = action.payload
        .split(',')
        .filter((perp) => perp.trim() !== '');
      return {
        ...state,
        perpetrators: perpArray.map((name) => ({
          name: name.trim(),
          external: true,
        })),
        evidence: {
          ...state.evidence,
          evidence: [
            ...state.evidence.evidence.filter(
              (e) => e.type !== EvidenceType.PERPETRATOR,
            ),
            ...perpArray.map((name) =>
              createEvidence(EvidenceType.PERPETRATOR, {
                name: name.trim(),
                external: true,
              }),
            ),
          ],
        },
      };
    }

    case OpenReportTypes.SET_EVENT_EMOTION: {
      const emotionIndex = state.evidence.evidence.findIndex(
        (e) => e.type === EvidenceType.EMOTION,
      );
      return {
        ...state,
        evidence: {
          ...state.evidence,
          evidence:
            emotionIndex > -1
              ? state.evidence.evidence.map((e, i) =>
                  i === emotionIndex ? { ...e, value: action.payload } : e,
                )
              : [
                  ...state.evidence.evidence,
                  createEvidence(EvidenceType.EMOTION, action.payload),
                ],
        },
      };
    }

    case OpenReportTypes.SET_EVENT_WITNESSES: {
      const witnessIndex = state.evidence.evidence.findIndex(
        (e) => e.type === EvidenceType.WITNESS,
      );
      return {
        ...state,
        evidence: {
          ...state.evidence,
          evidence:
            witnessIndex > -1
              ? state.evidence.evidence.map((e, i) =>
                  i === witnessIndex ? { ...e, value: action.payload } : e,
                )
              : [
                  ...state.evidence.evidence,
                  createEvidence(EvidenceType.WITNESS, action.payload),
                ],
        },
      };
    }

    case OpenReportTypes.SET_EVENT_LOCATION: {
      const locationIndex = state.evidence.evidence.findIndex(
        (e) => e.type === EvidenceType.LOCATION,
      );
      return {
        ...state,
        evidence: {
          ...state.evidence,
          evidence:
            locationIndex > -1
              ? state.evidence.evidence.map((e, i) =>
                  i === locationIndex ? { ...e, value: action.payload } : e,
                )
              : [
                  ...state.evidence.evidence,
                  createEvidence(EvidenceType.LOCATION, action.payload),
                ],
        },
      };
    }

    case OpenReportTypes.SET_SELECTED_COUNTRY:
      return {
        ...state,
        selectedCountryName: action.payload,
      };

    case OpenReportTypes.SET_ORGANISATION_COUNTRIES:
      return {
        ...state,
        countries: action.payload,
      };

    case OpenReportTypes.SET_SELECTED_SITE:
      return {
        ...state,
        selectedSiteName: action.payload,
      };

    case OpenReportTypes.SET_ORGANISATION_SITES:
      return {
        ...state,
        sites: action.payload,
      };

    case OpenReportTypes.SET_ORGANISATION:
      return {
        ...state,
        organisation: action.payload,
      };
    case OpenReportTypes.SET_ORGANISATION_LOGO:
      return {
        ...state,
        logo: action.payload,
      };
    case OpenReportTypes.SET_ORGANISATION_PRIVACY_STATEMENT:
      return {
        ...state,
        privacyStatementUrl: action.payload,
      };
    default:
      return state;
  }
};

export default openReportReducer;
